import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Pencil, Plus, FolderKanban, ChevronDown, Loader2, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { PageHeader } from "@/layouts/AdminLayout";
import { FormDialog } from "@/components/FormDialog";
import { StatusBadge, BuDot } from "@/components/StatusBadge";
import { EquipmentEditor, EquipmentReadList, toPayload } from "@/components/EquipmentLines";
import { PlannedImport } from "@/components/PlannedImport";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { clientFields, pick, projectFields, siteFields } from "@/lib/forms";
import { fmtDate } from "@/lib/format";
import { useList } from "@/lib/technicians";

const HistoryItem = ({ o }) => (
  <Collapsible className="rounded-lg border border-border bg-card/40" data-testid={`history-item-${o.folio}`}>
    <div className="flex flex-wrap items-center gap-3 px-4 py-3">
      <Link to={`/admin/os/${o.id}`} className="font-mono text-sm font-semibold text-primary hover:underline">{o.folio}</Link>
      <span className="text-sm">{o.service_type_name}</span>
      <span className="text-xs text-muted-foreground">{o.business_unit_name}{o.project_name ? ` · ${o.project_name}` : ""}</span>
      <span className="ml-auto flex items-center gap-3">
        <span className="font-mono text-xs text-muted-foreground">{fmtDate(o.closed_at || o.scheduled_date || o.created_at)}</span>
        <StatusBadge status={o.status} />
        <CollapsibleTrigger asChild><Button size="icon" variant="ghost" className="h-8 w-8" data-testid={`history-toggle-${o.folio}`}><ChevronDown className="h-4 w-4" /></Button></CollapsibleTrigger>
      </span>
    </div>
    <CollapsibleContent className="space-y-3 border-t border-border px-4 py-3">
      <EquipmentReadList lines={o.equipment} />
      {o.warranty?.end_date && <p className="text-xs text-muted-foreground">Garantía hasta {fmtDate(o.warranty.end_date)} · Próximo mantenimiento {fmtDate(o.maintenance?.next_date)}</p>}
    </CollapsibleContent>
  </Collapsible>
);

const PlannedDialog = ({ project, onOpenChange, onSaved }) => {
  const [lines, setLines] = useState([]);
  useEffect(() => setLines(project?.planned_equipment || []), [project]);
  const save = async () => {
    try {
      await api.put(`/projects/${project.id}/planned-equipment`, { lines: toPayload(lines) });
      toast.success("Equipamiento planeado guardado");
      onOpenChange(false);
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const importLines = async (added) => {
    try {
      const { data } = await api.put(`/projects/${project.id}/planned-equipment`, { lines: toPayload([...lines, ...added]) });
      setLines(data);
      toast.success(`${added.length} equipos importados al equipamiento planeado`);
      onSaved();
      return true;
    } catch (e) { toast.error(errMsg(e)); return false; }
  };
  return (
    <Dialog open={!!project} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="planned-equipment-dialog">
        <DialogHeader><DialogTitle className="font-display uppercase tracking-wide">Equipamiento planeado · {project?.name}</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">Lo que se planeó instalar o entregar. No es inventario y no modifica el catálogo. Las OS copian este listado como snapshot independiente.</p>
        {project && <PlannedImport projectId={project.id} onConfirm={importLines} />}
        <EquipmentEditor lines={lines} onChange={setLines} buCode={project?.business_unit_code} allowDescription />
        <DialogFooter><Button onClick={save} data-testid="planned-equipment-save-button">Guardar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function ClientDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { meta, can } = useAuth();
  const [client, setClient] = useState(null);
  const [projects, setProjects] = useState([]);
  const [history, setHistory] = useState([]);
  const [dlg, setDlg] = useState(null);
  const [zones] = useList("/zones");

  const load = useCallback(() => {
    api.get(`/clients/${id}`).then((r) => setClient(r.data));
    api.get("/projects", { params: { client_id: id } }).then((r) => setProjects(r.data));
    api.get(`/clients/${id}/history`).then((r) => setHistory(r.data));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!client) return <Loader2 className="mx-auto mt-24 h-6 w-6 animate-spin text-primary" />;
  const pFields = projectFields(meta, zones);

  const saveClient = async (form) => { await api.patch(`/clients/${id}`, form); toast.success("Cliente actualizado"); load(); };
  const saveProject = async (form) => {
    if (dlg?.project) await api.patch(`/projects/${dlg.project.id}`, form);
    else await api.post("/projects", { ...form, client_id: id });
    toast.success("Proyecto guardado");
    load();
  };
  const saveSite = async (form) => {
    if (dlg?.site) await api.patch(`/clients/${id}/sites/${dlg.site.id}`, form);
    else await api.post(`/clients/${id}/sites`, form);
    toast.success("Sitio guardado");
    load();
  };
  const sites = client.sites || [];

  return (
    <div>
      <Button variant="ghost" size="sm" className="mb-2 -ml-2 text-muted-foreground" onClick={() => nav("/admin/clientes")} data-testid="back-button"><ArrowLeft className="mr-1 h-4 w-4" />Clientes</Button>
      <PageHeader eyebrow="Cliente" title={client.name}>
        {can("clients.write") && <Button variant="outline" onClick={() => setDlg({ type: "client" })} data-testid="edit-client-button"><Pencil className="mr-2 h-4 w-4" />Editar</Button>}
        {can("wo.write") && <Button asChild data-testid="client-new-wo-button"><Link to={`/admin/os/nueva?client=${id}`}><Plus className="mr-1 h-4 w-4" />Nueva OS</Link></Button>}
      </PageHeader>
      <div className="grid gap-5 xl:grid-cols-[380px_1fr]">
        <div className="space-y-5">
          <section className="rounded-xl border border-border bg-card/40 p-5 text-sm" data-testid="client-info-card">
            {[["Contacto", client.contact_name], ["Teléfono", client.phone], ["Correo", client.email], ["Dirección", client.address], ["Estado", client.status], ["Observaciones", client.notes]].map(([k, v]) => (
              <div key={k} className="mb-3"><div className="text-[11px] uppercase tracking-wider text-muted-foreground">{k}</div><div className="whitespace-pre-wrap">{v || "—"}</div></div>
            ))}
          </section>
          <section className="rounded-xl border border-border bg-card/40 p-5" data-testid="client-sites-card">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Sitios ({sites.length})</h2>
              {can("clients.write") && <Button size="sm" variant="outline" onClick={() => setDlg({ type: "site" })} data-testid="new-site-button"><Plus className="mr-1 h-3 w-3" />Sitio</Button>}
            </div>
            <div className="space-y-2">
              {sites.length === 0 && <p className="text-sm text-muted-foreground">Sin sitios</p>}
              {sites.map((s) => (
                <div key={s.id} className="flex items-start gap-3 rounded-lg border border-border px-3 py-2.5" data-testid={`site-item-${s.id}`}>
                  <MapPin className="mt-0.5 h-4 w-4 text-primary" />
                  <div className="min-w-0 flex-1 text-sm">
                    <div className="font-medium">{s.name}</div>
                    <div className="text-xs text-muted-foreground">{s.address || "—"}</div>
                    <div className="text-xs text-muted-foreground">{[s.contact_name, s.phone].filter(Boolean).join(" · ") || "—"}</div>
                  </div>
                  {can("clients.write") && <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setDlg({ type: "site", site: s })} data-testid={`edit-site-${s.id}`}><Pencil className="h-3.5 w-3.5" /></Button>}
                </div>
              ))}
            </div>
          </section>
          <section className="rounded-xl border border-border bg-card/40 p-5" data-testid="client-projects-card">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Proyectos ({projects.length})</h2>
              {can("projects.write") && <Button size="sm" variant="outline" onClick={() => setDlg({ type: "project" })} data-testid="new-project-button"><Plus className="mr-1 h-3 w-3" />Proyecto</Button>}
            </div>
            <div className="space-y-2">
              {projects.length === 0 && <p className="text-sm text-muted-foreground">Sin proyectos</p>}
              {projects.map((p) => (
                <div key={p.id} className="flex items-center gap-3 rounded-lg border border-border px-3 py-2.5" data-testid={`project-item-${p.id}`}>
                  <FolderKanban className="h-4 w-4 text-primary" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{p.name}</div>
                    <div className="text-xs text-muted-foreground"><BuDot meta={meta} code={p.business_unit_code} /> · {p.work_orders_count} OS</div>
                  </div>
                  {can("wo.write") && <Button size="sm" variant="ghost" asChild data-testid={`project-new-wo-${p.id}`}><Link to={`/admin/os/nueva?client=${id}&project=${p.id}`}>+ OS</Link></Button>}
                  {can("projects.write") && <Button size="sm" variant="ghost" onClick={() => setDlg({ type: "planned", project: p })} data-testid={`project-planned-${p.id}`}>Equipamiento planeado ({(p.planned_equipment || []).length})</Button>}
                  {can("projects.write") && <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setDlg({ type: "project", project: p })} data-testid={`edit-project-${p.id}`}><Pencil className="h-3.5 w-3.5" /></Button>}
                </div>
              ))}
            </div>
          </section>
        </div>
        <section data-testid="client-history">
          <h2 className="mb-4 font-display text-xl font-semibold">Historial de servicios</h2>
          {history.length === 0 && <p className="text-sm text-muted-foreground">Este cliente aún no tiene órdenes de servicio.</p>}
          <div className="space-y-6">
            {history.map((g) => (
              <div key={g.year}>
                <div className="mb-2 font-mono text-sm font-bold text-primary">{g.year}</div>
                <div className="space-y-2">{g.orders.map((o) => <HistoryItem key={o.id} o={o} />)}</div>
              </div>
            ))}
          </div>
        </section>
      </div>
      <PlannedDialog project={dlg?.type === "planned" ? dlg.project : null} onOpenChange={(o) => !o && setDlg(null)} onSaved={load} />
      <FormDialog open={dlg?.type === "client"} onOpenChange={(o) => !o && setDlg(null)} title="Editar cliente" fields={clientFields} initial={pick(client, clientFields)} onSubmit={saveClient} testid="client-form" />
      <FormDialog open={dlg?.type === "site"} onOpenChange={(o) => !o && setDlg(null)} title={dlg?.site ? "Editar sitio" : "Nuevo sitio"} fields={siteFields} initial={dlg?.site ? pick(dlg.site, siteFields) : {}} onSubmit={saveSite} testid="site-form" />
      <FormDialog open={dlg?.type === "project"} onOpenChange={(o) => !o && setDlg(null)} title={dlg?.project ? "Editar proyecto" : "Nuevo proyecto"} fields={pFields}
        initial={dlg?.project ? pick(dlg.project, pFields) : { business_unit_code: meta?.business_units?.[0]?.code }} onSubmit={saveProject} testid="project-form" />
    </div>
  );
}
